import { useMutation } from '@tanstack/react-query'
import { Spinner } from '~/shadcn/ui/spinner'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { useUser } from '~/@biz_account/useUser/useUser'
import { useUserNotifications } from '~/@biz_notifications/user-notifications/useUserNotifications'
import { getInvestorAnalysisVersion } from '../../investor-analysis/analysis-versions/investorAnalysisVersions'
import { useInvestorAnalysisStore } from '../../investor-analysis/stage-analysis/useInvestorAnalysisStore'
import { useInvestorAnalysis } from '../../investor-analysis/useInvestorAnalysis'
import { SophisticatedInvestorSignAgreement } from '../stage-sign-agreement/SophisticatedInvestorSignAgreement'
import { UnsophisticatedInvestorSignAgreement } from '../stage-sign-agreement/UnsophisticatedInvestorSignAgreement'
import { SigningProgressScreen } from '../stage-signing-progress/SigningProgressScreen'
import { useInvestorAnalysisResult } from '../useInvestorAnalysisResult'
import { useInvestorSignAgreement } from '../useInvestorSignAgreement'

type Props = {
  onContractSigned: () => void
}

function InvestorSignAgreement(props: Props) {
  const { useSophisticatedTestStore, useUnsophisticatedTestStore } =
    getInvestorAnalysisVersion()
  const { result: analysisResult, isLoading: isLoadingAnalysisResult } =
    useInvestorAnalysisResult()
  const { reloadNotifications } = useUserNotifications()
  const { resetInvestorAnalysis } = useInvestorAnalysis()
  const { finishRegistration } = useInvestorSignAgreement()

  const { user, reloadUser } = useUser()
  const analysisStore = useInvestorAnalysisStore(user?._id ?? null)
  const unsophisticatedTestStore = useUnsophisticatedTestStore(
    user?._id ?? null
  )
  const sophisticatedTestStore = useSophisticatedTestStore(user?._id ?? null)

  const finishRegistrationProcess = useMutation({
    mutationFn: finishRegistration,
  })

  function resetInvestorAnalysisFormSteps() {
    unsophisticatedTestStore.reset()
    sophisticatedTestStore.reset()
  }

  function removeInvestorAnalysisData() {
    unsophisticatedTestStore.remove()
    sophisticatedTestStore.remove()
    analysisStore.remove()
  }

  function handleSignAgreement() {
    finishRegistrationProcess.mutate(undefined, {
      onSuccess: async () => {
        Analytics.track('user-signed-main-agreement')

        removeInvestorAnalysisData()

        await reloadNotifications()
        await reloadUser()

        props.onContractSigned()
      },
    })
  }

  // `isSuccess` drží loading screen aj počas `reloadNotifications` + `reloadUser` v onSuccess
  // (mutácia už nie je pending, ale ešte sme nenavigovali preč) — inak by formulár na chvíľu preblikol.
  const isFinishingRegistration =
    finishRegistrationProcess.isPending || finishRegistrationProcess.isSuccess

  const content = (() => {
    if (isFinishingRegistration) {
      return <SigningProgressScreen />
    }

    if (user?.profile?.investorInfo?.type === 'sophisticated') {
      return (
        <SophisticatedInvestorSignAgreement
          isLoading={finishRegistrationProcess.isPending}
          onSign={handleSignAgreement}
          onUnsophisticated={() => {
            resetInvestorAnalysisFormSteps()
            // user sa vzdal statusu sofistikovaného = odpoveď „Nie“ na úvodnú otázku
            analysisStore.setIsSophisticatedInvestor(false)
            analysisStore.setFinishedScreen(null)
            analysisStore.setStage('assessExperience')
            void resetInvestorAnalysis()
          }}
        />
      )
    }

    if (user?.profile?.investorInfo?.type === 'unsophisticated') {
      if (isLoadingAnalysisResult) {
        return (
          <div className="flex justify-center items-center h-full">
            <Spinner className="size-10" />
          </div>
        )
      }

      return (
        <UnsophisticatedInvestorSignAgreement
          isExperiencedInvestor={analysisResult?.score !== 'F'}
          nettoAssets={analysisResult?.nettoAssets ?? null}
          riskTolerance={analysisResult?.riskTolerance ?? null}
          isLoading={finishRegistrationProcess.isPending}
          onSign={handleSignAgreement}
        />
      )
    }

    return null
  })()

  return <>{content} </>
}

export { InvestorSignAgreement }
